import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, Plus, Copy, Trash2, DollarSign, Link2, Pencil, Loader2, TrendingUp, Send, FileText,
} from "lucide-react";

/**
 * Affiliate / Influencer / Endorser program admin.
 *
 * Two panes: aggregated totals ribbon (clicks/signups/subs/commission due) +
 * the affiliate table (one row per person we've partnered with). Each row
 * links to a "Payout" dialog and shows the exact `?aff=<slug>` link they
 * should share.
 *
 * Backend: /api/admin/affiliates (list/create/patch/delete/payout).
 */
export default function AffiliatesAdminCard() {
  const [data, setData] = useState({ affiliates: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(null); // affiliate row
  const [editOpen, setEditOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/affiliates");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const linkFor = (slug) => `${origin}/?aff=${slug}`;

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(linkFor(slug));
      toast.success(`Copied ${linkFor(slug)}`);
    } catch { toast.error("Could not copy — select and copy manually"); }
  };

  const deactivate = async (a) => {
    if (!window.confirm(`Deactivate "${a.name}"? Their link stops attributing new signups. Historical stats stay.`)) return;
    try {
      await api.delete(`/admin/affiliates/${a.id}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const T = data.totals || {};

  return (
    <div className="space-y-4" data-testid="affiliates-admin-card">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <h2 className="serif text-2xl">Affiliates & Influencers</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Custom tracking links for influencers, endorsers and channel partners.
                <b> Every affiliate gets their own commission rate</b> — set it in the create/edit dialog.
                Commissions accrue monthly based on referred users who have an active paid DPP Pro subscription. Rate is set individually per affiliate (see §4 of the Affiliate T&Cs).
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm("Send the T&Cs update / 30-day notice email to every active affiliate with an email on file? Their `terms_notice_sent_at` timestamp will be recorded — this is what starts the 30-day clock before you can change any commission rate under §4.1.")) return;
                  try {
                    const r = await api.post("/admin/affiliates/send-terms-notice");
                    toast.success(`Sent ${r.data.emailed} T&Cs notice${r.data.emailed === 1 ? "" : "s"} · ${r.data.skipped_no_email} skipped (no email)`);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Terms notice send failed"); }
                }}
                className="rounded-full"
                data-testid="affiliate-send-terms-notice"
                title="Kicks off the §4.1 30-day rate-change notice clock"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Send T&Cs update
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm("Send monthly recap emails to every active affiliate with an email on file?")) return;
                  try {
                    const r = await api.post("/admin/affiliates/send-monthly-recaps");
                    toast.success(`Sent ${r.data.emailed} recap${r.data.emailed === 1 ? "" : "s"} · ${r.data.skipped_no_email} skipped (no email)`);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Recap send failed"); }
                }}
                className="rounded-full"
                data-testid="affiliate-send-recaps"
              >
                <Send className="w-4 h-4 mr-1.5" /> Send monthly recaps
              </Button>
              <Button onClick={() => setCreateOpen(true)} className="rounded-full" data-testid="affiliate-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New affiliate
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Active affiliates" value={`${T.active_count || 0} / ${T.affiliates_count || 0}`} />
            <Stat label="Total clicks"       value={(T.total_clicks || 0).toLocaleString()} />
            <Stat label="Signups"            value={(T.total_signups || 0).toLocaleString()} />
            <Stat label="Attributed users"   value={(T.total_attributed_users || 0).toLocaleString()} />
            <Stat label="MAU (30d)"          value={(T.total_mau_30d || 0).toLocaleString()} accent />
            <Stat label="Monthly proj."      value={`$${(T.total_monthly_commission_projection || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} accent />
            <Stat label="Commission paid"    value={`$${(T.total_commission_paid || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} />
            <Stat label="Commission DUE"     value={`$${(T.total_commission_due || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} accent />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="affiliate-export-csv"
              onClick={() => {
                const token = localStorage.getItem("dp_token") || "";
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/affiliates-attribution.csv`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = "dpp-affiliate-attribution.csv";
                    a.click();
                    URL.revokeObjectURL(a.href);
                    toast.success("Attribution CSV downloaded");
                  })
                  .catch(() => toast.error("Export failed"));
              }}
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Export attribution CSV
            </Button>
            <div className="mono text-[10px] text-muted-foreground self-center">
              One row per referred user · signup date · lifetime revenue · MAU flag
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : data.affiliates.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No affiliates yet. Tap <b>New affiliate</b> above to create the first tracking link.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]">
                <thead className="bg-muted/60 mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Affiliate</th>
                    <th className="text-left px-3 py-2">Link</th>
                    <th className="text-right px-3 py-2" title="Per paid Pro user attributed to this affiliate. Individually agreed per §4 of the Affiliate T&Cs.">$/Pro user</th>
                    <th className="text-right px-3 py-2">Clicks</th>
                    <th className="text-right px-3 py-2">Users</th>
                    <th className="text-right px-3 py-2">MAU 30d</th>
                    <th className="text-right px-3 py-2">Monthly</th>
                    <th className="text-right px-3 py-2">Paid $</th>
                    <th className="text-right px-3 py-2">Due $</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.affiliates.map((a) => (
                    <tr key={a.id} className={`border-t border-border ${!a.active ? "opacity-50" : ""}`} data-testid={`affiliate-row-${a.slug}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium flex items-center gap-1.5">
                          {a.name}
                          {a.pending_review && (
                            <Badge variant="outline" className="mono text-[9px] uppercase text-secondary border-secondary/40" title="Applied via /affiliate — review commission tier and confirm">
                              Pending review
                            </Badge>
                          )}
                        </div>
                        <div className="mono text-[10px] text-muted-foreground">{a.email || "no email"} · {a.slug}</div>
                        {!a.active && <Badge variant="outline" className="mono text-[9px] uppercase mt-1">Inactive</Badge>}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => copyLink(a.slug)}
                          className="inline-flex items-center gap-1.5 mono text-[10px] text-muted-foreground hover:text-primary transition-colors max-w-[180px] truncate"
                          data-testid={`affiliate-copy-${a.slug}`}
                          title={linkFor(a.slug)}
                        >
                          <Copy className="w-3 h-3 shrink-0" /> <span className="truncate">{linkFor(a.slug).replace(/^https?:\/\//, "")}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2 mono text-right">${(a.commission_per_mau ?? 1).toFixed(2)}</td>
                      <td className="px-3 py-2 mono text-right">{a.clicks}</td>
                      <td className="px-3 py-2 mono text-right">{a.attributed_users || 0}</td>
                      <td className="px-3 py-2 mono text-right text-primary font-semibold">{a.mau_30d || 0}</td>
                      <td className="px-3 py-2 mono text-right text-primary">${(a.monthly_commission_projection || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 mono text-right text-muted-foreground">${a.commission_paid.toFixed(2)}</td>
                      <td className="px-3 py-2 mono text-right font-semibold text-secondary">${a.commission_due.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditOpen(a)} data-testid={`affiliate-edit-${a.slug}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setPayoutOpen(a)} disabled={a.commission_due <= 0} data-testid={`affiliate-payout-${a.slug}`}>
                            <DollarSign className="w-3.5 h-3.5" />
                          </Button>
                          {a.active && (
                            <Button size="sm" variant="ghost" onClick={() => deactivate(a)} data-testid={`affiliate-deactivate-${a.slug}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateAffiliateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => { setCreateOpen(false); load(); }} />
      <PayoutDialog affiliate={payoutOpen} onClose={() => setPayoutOpen(null)} onDone={() => { setPayoutOpen(null); load(); }} />
      <EditAffiliateDialog affiliate={editOpen} onClose={() => setEditOpen(null)} onDone={() => { setEditOpen(null); load(); }} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`serif text-lg mt-0.5 ${accent ? "text-secondary" : ""}`}>{value}</div>
    </div>
  );
}

function CreateAffiliateDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState("");
  const [pct, setPct] = useState(25);
  const [perMau, setPerMau] = useState(1.00);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(""); setEmail(""); setSlug(""); setPct(25); setPerMau(1.00); setNotes(""); } }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/affiliates", {
        name: name.trim(), email: email.trim() || undefined,
        slug: slug.trim() || undefined,
        commission_pct: Number(pct),
        commission_per_mau: Number(perMau),
        notes: notes.trim() || undefined,
      });
      toast.success(`"${name}" created`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const preview = slug.trim() ? `${origin}/?aff=${slug.trim().toLowerCase()}` : `${origin}/?aff=${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "auto-generated"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="affiliate-create-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">New affiliate</DialogTitle>
          <DialogDescription>Give them a display name, an optional email, a custom slug (or we auto-generate one), and a commission % that applies to every paid subscription attributed to them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Display name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. @DivStarInfluencer" data-testid="affiliate-name" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="star@youtube-partner.com" data-testid="affiliate-email" />
          </div>
          <div className="grid grid-cols-[2fr,1fr,1fr] gap-3">
            <div>
              <Label>Custom URL slug (optional)</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="auto" data-testid="affiliate-slug" />
            </div>
            <div>
              <Label>$ per paid Pro user / mo</Label>
              <Input type="number" step="0.25" min="0" max="25" value={perMau} onChange={(e) => setPerMau(e.target.value)} data-testid="affiliate-per-mau" />
              <div className="mono text-[10px] text-muted-foreground mt-1">Up to $1.00 · negotiated per creator</div>
            </div>
            <div>
              <Label>Sub % (legacy)</Label>
              <Input type="number" step="0.5" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="affiliate-pct" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Channel, audience size, negotiated terms…" data-testid="affiliate-notes" />
          </div>
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><Link2 className="w-3 h-3" /> Their shareable link</div>
            <div className="mono text-[11px] break-all">{preview}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="affiliate-create-submit" className="rounded-full">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayoutDialog({ affiliate, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (affiliate) { setAmount(affiliate.commission_due.toFixed(2)); setNotes(""); }
  }, [affiliate]);
  if (!affiliate) return null;

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/affiliates/${affiliate.id}/payout`, { amount: amt, notes: notes.trim() || undefined });
      toast.success(`Logged $${amt.toFixed(2)} payout to ${affiliate.name}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="affiliate-payout-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Record payout</DialogTitle>
          <DialogDescription>
            <b>{affiliate.name}</b> — commission due <span className="text-secondary mono">${affiliate.commission_due.toFixed(2)}</span>.
            Logs the payout so the running "due" number goes down. This tool doesn't move money — pay via your normal channel (bank transfer, PayPal, etc.) and log the amount here.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount ($)</Label>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payout-amount" />
          </div>
          <div>
            <Label>Notes (reference, method, etc.)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Bank transfer, ref TXN12345" data-testid="payout-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="payout-submit" className="rounded-full">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging…</> : "Record payout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAffiliateDialog({ affiliate, onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pct, setPct] = useState(25);
  const [perMau, setPerMau] = useState(1.00);
  const [active, setActive] = useState(true);
  const [pendingReview, setPendingReview] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (affiliate) {
      setName(affiliate.name); setEmail(affiliate.email || ""); setPct(affiliate.commission_pct);
      setPerMau(affiliate.commission_per_mau ?? 1.00);
      setActive(!!affiliate.active); setPendingReview(!!affiliate.pending_review); setNotes(affiliate.notes || "");
    }
  }, [affiliate]);
  if (!affiliate) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/affiliates/${affiliate.id}`, {
        name: name.trim(), email: email.trim() || null,
        commission_pct: Number(pct),
        commission_per_mau: Number(perMau),
        active, pending_review: pendingReview,
        notes: notes.trim() || null,
      });
      toast.success("Saved");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="affiliate-edit-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Edit {affiliate.name}</DialogTitle>
          <DialogDescription>Slug can't change — it's the shared link. Everything else is editable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Display name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-affiliate-name" /></div>
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-affiliate-email" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>$ per paid Pro user / mo</Label>
              <Input type="number" step="0.25" min="0" max="25" value={perMau} onChange={(e) => setPerMau(e.target.value)} data-testid="edit-affiliate-per-mau" />
              <div className="mono text-[10px] text-muted-foreground mt-1">
                Set individually per §4 · up to $1.00
              </div>
            </div>
            <div><Label>Sub % (legacy)</Label><Input type="number" step="0.5" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="edit-affiliate-pct" /></div>
            <div>
              <Label>Status</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={active ? "1" : "0"} onChange={(e) => setActive(e.target.value === "1")} data-testid="edit-affiliate-active">
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={pendingReview} onChange={(e) => setPendingReview(e.target.checked)} className="mt-0.5" data-testid="edit-affiliate-pending" />
            <span>
              <b>Pending review</b>
              <div className="text-xs text-muted-foreground">Flag as still-to-be-vetted (self-serve applicant). Uncheck once you've confirmed their commission tier.</div>
            </span>
          </label>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-affiliate-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="edit-affiliate-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
